import React, { useState, useEffect } from 'react'
import {
  Dropdown,
  IBasePicker,
  ITag,
  MessageBarType,
  Stack,
  TextField,
  Toggle,
} from 'office-ui-fabric-react'
import InfoBox from './InfoBox'
import { TagInput } from '../../../Components/Input/TagInput'
import { gamBannerSizes } from '../../../Constants/GamBannerSizes'
import {
  BannerScriptExecutionTiming,
  CampaignGamBannerAssetInfo,
} from '../../../Api/campaigns'
import { Currencies } from '../../../Constants/Currencies'
import { NumberInput } from '../../../Components/Input/NumberInput'

interface GamBannerFormProps {
  errors: {
    slotId: string | undefined
    sizes: string | undefined
    autoRefreshEverySeconds: string | undefined
    executionTiming: string | undefined
    minimumPriceFloor?: string
    currency?: string
  }
  values: CampaignGamBannerAssetInfo
  onChange: (_: CampaignGamBannerAssetInfo) => void
  disabled: boolean
  showPricingControls?: boolean
}

const SIZES_TAGS: ITag[] = gamBannerSizes.map((size) => ({
  key: size,
  name: size,
}))
const DEFAULT_SIZES: string[] = gamBannerSizes
  .map((size) => size.split('x').map(Number))
  .filter(
    ([width, height]) =>
      width >= 150 &&
      width <= 300 &&
      height <= 300 &&
      width / height >= 2 / 3 &&
      width / height <= 16 / 9
  )
  .map((size) => size.join('x'))

const GamBannerForm: React.FC<GamBannerFormProps> = ({
  errors,
  values,
  onChange,
  disabled,
  showPricingControls,
}: GamBannerFormProps) => {
  const [showAutoRefreshInput, setShowAutoRefreshInput] = useState(
    values.autoRefreshEverySeconds != null
  )
  const [tagInputComponent, setTagInputComponent] =
    useState<IBasePicker<ITag> | null>(null)
  const onResolveSuggestions = (filter: string) => {
    const sizeSuggestion = SIZES_TAGS.filter(
      (size) =>
        !values.sizes.includes(size.key) &&
        (size.name.includes(filter) || size.key.includes(filter))
    )
    const filterSanitized = filter.replace(/\s+/g, '')
    const [width, height] = filterSanitized
      .split('x')
      .map((n) => Math.abs(parseInt(n)))
    const key = width + 'x' + height
    if (width && height && !values.sizes.includes(key)) {
      sizeSuggestion.push({ key, name: `Add "${key}"` })
    }
    return sizeSuggestion
  }
  const selectedSizes = unique(values.sizes)
    .filter((size) => size !== 'fluid')
    .map((size) => ({
      key: size,
      name: size,
    }))
  useEffect(() => {
    if (!!values.sizes.length) {
      return
    }
    onChange({
      ...values,
      sizes: DEFAULT_SIZES,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // correctly send undefined for optional fields when not set
  if (showPricingControls) {
    values.minimumPriceFloor = values.minimumPriceFloor || 0
    values.currency = values.currency || Currencies.CZK
  } else {
    values.minimumPriceFloor = undefined
    values.currency = undefined
  }

  return (
    <>
      <TextField
        label="Slot ID"
        errorMessage={errors.slotId}
        value={values.slotId}
        autoComplete="off"
        spellCheck={false}
        onChange={(evt: any, newValue?: string) =>
          onChange({
            ...values,
            slotId: newValue || '',
          })
        }
        disabled={disabled}
        resizable={false}
        required
      />
      <InfoBox type={MessageBarType.info}>
        The slot id is to the id that appears after the{' '}
        <code>googletag.defineSlot{'('}"..."</code> part of the embed script.
        <br />
        It should inputted without the quotes and should start with the /
        character. The player will auto-generate the ideal code to integrate for
        data collection and prioritization logic.
        <br />
        EX: <code>/32143212/abcdef.com/abcdef.com_caroda_slot</code>
      </InfoBox>
      <TagInput
        componentRef={(ref) => {
          setTagInputComponent(ref)
        }}
        label="Allowed Sizes"
        onResolveSuggestions={onResolveSuggestions}
        onChange={async (tags = []) => {
          onChange({
            ...values,
            sizes: tags.map(({ key }) => key),
          })
          // This is required to keep the suggestions panel open
          const tagInputComponentAsAny = tagInputComponent as any
          await waitMs(0)
          tagInputComponentAsAny.setState({ suggestionsVisible: true })
          const suggestions = onResolveSuggestions('')
          const suggestionsMinusLastTags = suggestions.filter(
            (suggestion) => !tags.some((tag) => tag.key === suggestion.key)
          )
          tagInputComponentAsAny.updateSuggestions(suggestionsMinusLastTags)
        }}
        selectedItems={selectedSizes}
        disabled={disabled}
        pickerLabels={{
          suggestionsHeader: 'Suggested sizes',
          noResults: 'No size found',
        }}
        errorMessage={errors.sizes}
        itemLimit={9999}
        required
      />
      <InfoBox type={MessageBarType.info}>
        Note that Caroda will always filter out any size bigger than the maximum
        available width for a position, regardless of the configured filter.
      </InfoBox>
      <Dropdown
        label="Execution Timing"
        errorMessage={errors.executionTiming}
        selectedKey={
          values.executionTiming && values.executionTiming.toString()
        }
        onChange={(_, value?) => {
          if (!value) {
            return
          }
          const newExecutionTiming =
            value.key ===
            BannerScriptExecutionTiming.AS_SOON_AS_POSSIBLE.toString()
              ? BannerScriptExecutionTiming.AS_SOON_AS_POSSIBLE
              : BannerScriptExecutionTiming.WHEN_IN_VIEW
          onChange({
            ...values,
            executionTiming: newExecutionTiming,
          })
        }}
        options={[
          {
            key: BannerScriptExecutionTiming.AS_SOON_AS_POSSIBLE,
            text: 'As soon as possible',
          },
          {
            key: BannerScriptExecutionTiming.WHEN_IN_VIEW,
            text: 'When in view',
          },
        ]}
        placeholder="Select an option"
        disabled={disabled}
      />
      <Toggle
        label="Auto-Refresh"
        disabled={disabled}
        checked={showAutoRefreshInput}
        onChange={(_, checked: boolean | undefined) => {
          setShowAutoRefreshInput(!!checked)
          if (!checked) {
            onChange({
              ...values,
              autoRefreshEverySeconds: undefined,
            })
          }
        }}
      />
      {showAutoRefreshInput && (
        <TextField
          label="Interval (seconds)"
          type="number"
          errorMessage={errors.autoRefreshEverySeconds}
          value={
            values.autoRefreshEverySeconds
              ? values.autoRefreshEverySeconds.toString()
              : ''
          }
          autoComplete="off"
          spellCheck={false}
          onChange={(_: any, newValue?: string) =>
            onChange({
              ...values,
              autoRefreshEverySeconds: newValue
                ? parseInt(newValue)
                : undefined,
            })
          }
          disabled={disabled}
          resizable={false}
          required
          min={10}
          max={120}
        />
      )}
      {showAutoRefreshInput && (
        <InfoBox type={MessageBarType.info}>
          The position will request a new banner every N seconds. It will not
          refresh more than once when out of view.
        </InfoBox>
      )}
      {showPricingControls && (
        <>
          <Stack horizontal>
            <NumberInput
              label="Minimum Price Floor"
              style={{
                flexGrow: 1,
                maxWidth: 'calc(50% - 8px)',
              }}
              errorMessage={errors.minimumPriceFloor}
              value={values.minimumPriceFloor!}
              onChange={(newValue) =>
                onChange({
                  ...values,
                  minimumPriceFloor: newValue,
                })
              }
              disabled={disabled}
              precision={2}
            />
            <div style={{ width: 16 }} />
            <Dropdown
              label="Currency"
              styles={{
                root: {
                  flexGrow: 1,
                  position: 'relative',
                },
              }}
              errorMessage={errors.currency}
              selectedKey={values.currency}
              onChange={(_, option) => {
                if (option) {
                  onChange({
                    ...values,
                    currency: option.key as Currencies,
                  })
                }
              }}
              options={[
                {
                  key: Currencies.CZK,
                  text: Currencies.CZK,
                },
              ]}
              disabled={true}
            />
          </Stack>
          <InfoBox type={MessageBarType.info}>
            The value will be converted to CZK, bucketed and send as the value
            of the FloorPrice targeting key to GAM.
          </InfoBox>
        </>
      )}
    </>
  )
}

export default GamBannerForm

async function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function unique<T>(array: T[]): T[] {
  return Array.from(new Set(array))
}
